/**
 * VENDOR MODULES
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

import EVENT_NAMES from 'shared/constants/events';
import Emitter from 'shared/events/emitter';
import { cn } from 'lib/utils';
import { Modal } from 'shared/components/ui/modal';

function DialogBox() {
  const { t } = useTranslation();
  const [show, setShow] = React.useState(false);
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [closeCallback, setCloseCallback] = React.useState<{ callback: (() => void) | null }>({
    callback: null,
  });

  React.useEffect(() => {
    let timerRef: ReturnType<typeof setTimeout> | null = null;

    const handleShow = (payload: {
      title?: string;
      message?: string;
      close_sec?: number;
      on_close_callback?: () => void;
    }) => {
      setTitle(payload.title !== undefined ? payload.title : '');
      setMessage(payload.message !== undefined ? payload.message : '');
      setShow(true);
      setCloseCallback({ callback: payload.on_close_callback !== undefined ? payload.on_close_callback : null });

      if (timerRef) {
        clearTimeout(timerRef);
        timerRef = null;
      }

      const closeSec = payload.close_sec;
      if (typeof closeSec === 'number' && closeSec > 0) {
        timerRef = setTimeout(() => {
          closeSelf();
          timerRef = null;
        }, closeSec * 1000);
      }
    };

    const handleHide = () => {
      setTitle('');
      setMessage('');
      setShow(false);

      if (timerRef) {
        clearTimeout(timerRef);
        timerRef = null;
      }
    };

    Emitter.on(EVENT_NAMES.DIALOG.SHOW, handleShow);
    Emitter.on(EVENT_NAMES.DIALOG.HIDE, handleHide);

    return () => {
      Emitter.off(EVENT_NAMES.DIALOG.SHOW, handleShow);
      Emitter.off(EVENT_NAMES.DIALOG.HIDE, handleHide);
      if (timerRef) {
        clearTimeout(timerRef);
      }
    };
  }, []);

  React.useEffect(() => {
    if (!show && closeCallback.callback) {
      if (typeof closeCallback.callback === 'function') {
        closeCallback.callback();
        setCloseCallback({ callback: null });
      }
    }
  }, [show, closeCallback]);

  const closeSelf = () => {
    Emitter.emit(EVENT_NAMES.DIALOG.HIDE, {});
  };

  const getIconType = () => {
    const titleLower = title.toLowerCase();
    const messageLower = message.toLowerCase();

    if (
      titleLower.includes('error') ||
      titleLower.includes('double bet') ||
      messageLower.includes('error')
    ) {
      return {
        icon: 'bi-exclamation-triangle',
        bgColor: 'bg-red-100',
        iconColor: 'text-red-600',
      };
    }
    if (titleLower.includes('success') || messageLower.includes('success')) {
      return {
        icon: 'bi-check-circle',
        bgColor: 'bg-green-100',
        iconColor: 'text-green-600',
      };
    }
    return {
      icon: 'bi-info-circle',
      bgColor: 'bg-blue-100',
      iconColor: 'text-blue-600',
    };
  };

  const iconConfig = getIconType();

  const iconElement = (
    <div
      className={cn(
        'w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0',
        iconConfig.bgColor
      )}
    >
      <i
        className={cn('bi', iconConfig.icon, iconConfig.iconColor, 'text-lg')}
        aria-hidden
      />
    </div>
  );

  return (
    <Modal
      open={show}
      onClose={closeSelf}
      title={title}
      description={
        <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
          {message}
        </p>
      }
      icon={iconElement}
      size="md"
      footer={
        <button
          type="button"
          onClick={closeSelf}
          className={cn(
            'px-6 py-2 text-sm font-medium rounded-md transition-all duration-200',
            'bg-primary text-primary-foreground hover:opacity-90',
            'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'
          )}
        >
          {t('OK') || 'OK'}
        </button>
      }
    />
  );
}

export default DialogBox;
